import {useCallback, useMemo} from 'react';
import {useForm} from 'react-hook-form';
import {
  useGuestGroup,
  useMutateReservation,
  usePetFeeDeal,
  useReservationSettings,
  useTaxes,
} from 'hooks';
import {useSummary} from 'context/summary';
import {useReservation} from 'context/reservation';
import {useGuestGroupMutation} from 'hooks/useGuestGroupMutation';
import {markModalAsShown, useOnboardingStore} from '@chekin/shared';
import {getAgeLimitByCountry, getFields, getGroupType} from 'utils/reservationSettings';
import {FORM_NAMES} from 'utils/reservationSettings/constants';
import {SHOWED_GUEST_GROUP_MODAL} from 'utils/constants';
import {FormTypes} from 'components/ReservationSettingsModal/types';

const EMPTY_COUNTERS = {
  [FORM_NAMES.junior]: 0,
  [FORM_NAMES.middle]: 0,
  [FORM_NAMES.senior]: 0,
  [FORM_NAMES.numberOfGuests]: 0,
  [FORM_NAMES.numberOfPets]: 0,
};

function useGuestCountSubmit({onDone}: {onDone: () => void}) {
  const {seasonEntity, isFetching: isTaxesFetching} = useTaxes();
  const {housing, data: reservation} = useReservation();
  const {data: summary} = useSummary();
  const groupType = useOnboardingStore(state => state.groupType);

  const {
    guestGroup,
    isLoading: isGuestGroupLoading,
    isFetching: isGuestGroupFetching,
  } = useGuestGroup();
  const {updateGuestGroup: patchGuestGroup, isLoading: isGuestGroupMutation} =
    useGuestGroupMutation();
  const {mutate: updateReservation, isPending: isReservationMutation} =
    useMutateReservation();
  const {createPetFeeDeal, hasPetFeeOffer, isCreatingPetFeeDeal, isLoadingPetFeeOffer} =
    usePetFeeDeal();
  const {checkCanEditReservationDetails} = useReservationSettings();

  const formMethods = useForm<FormTypes>({defaultValues: EMPTY_COUNTERS});

  const isActiveTaxes = summary?.taxes?.active;
  const countryCode = summary?.country_code;

  const visibleFields = useMemo(
    () =>
      getFields({
        lessThanRule: seasonEntity.lessThanRule,
        betweenRule: seasonEntity.betweenRule,
        moreThanRule: seasonEntity.moreThanRule,
      }),
    [seasonEntity],
  );

  const validateGuestAge = useMemo(() => {
    if (isActiveTaxes || !countryCode) {
      return {ageLimit: null};
    }

    return getAgeLimitByCountry(countryCode, housing?.police_account_type);
  }, [countryCode, isActiveTaxes, housing]);

  const isLoading =
    isGuestGroupLoading ||
    isGuestGroupFetching ||
    isGuestGroupMutation ||
    isTaxesFetching ||
    isLoadingPetFeeOffer ||
    isCreatingPetFeeDeal ||
    isReservationMutation;

  const onSubmit = useCallback(
    (data: FormTypes) => {
      const senior = visibleFields[FORM_NAMES.senior] ? data[FORM_NAMES.senior] : 0;
      const middle = visibleFields[FORM_NAMES.middle] ? data[FORM_NAMES.middle] : 0;
      const junior = visibleFields[FORM_NAMES.junior] ? data[FORM_NAMES.junior] : 0;
      const numberOfGuests = visibleFields[FORM_NAMES.numberOfGuests]
        ? data[FORM_NAMES.numberOfGuests]
        : 0;

      patchGuestGroup(
        {
          seniors: senior,
          adults: middle || numberOfGuests,
          children: junior,
          group_type: getGroupType(numberOfGuests, groupType),
        },
        {
          onSuccess: () => {
            const numberOfPets = data[FORM_NAMES.numberOfPets] ?? 0;

            if (hasPetFeeOffer) {
              updateReservation({payload: {number_of_pets: numberOfPets}});
            }

            createPetFeeDeal(numberOfPets);
            markModalAsShown(SHOWED_GUEST_GROUP_MODAL, reservation?.id);
            onDone();
          },
        },
      );
    },
    [
      visibleFields,
      patchGuestGroup,
      groupType,
      reservation?.id,
      createPetFeeDeal,
      hasPetFeeOffer,
      updateReservation,
      onDone,
    ],
  );

  return {
    formMethods,
    onSubmit: formMethods.handleSubmit(onSubmit),
    formProps: {
      seasonEntity,
      guestGroup,
      ageLimit: validateGuestAge.ageLimit,
      isLoading,
      canEdit: checkCanEditReservationDetails(),
      showPetsCounter: hasPetFeeOffer,
    },
  };
}

export {useGuestCountSubmit};
